import { Card, CardContent } from '../core/Card';
import { useAdminDashboard } from '../../graphql/hooks/useAdminDashboard';
import { useActiveUsers } from '../../graphql/hooks/useUserDashboard';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { OvertimeGraph } from './DashboardOvertimeChart';
import { SickdayGraph } from './DashboardSickdayChart';
import { HolidayGraph } from './DashboardHolidayChart';
import { SearchableSelect } from '../core/SearchableSelect';
import useDismiss from '../custome_hooks/useDismiss';

export function AdminDashboardView() {
  const { data: users } = useActiveUsers();
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const userOptions = useMemo(
    () => (users ?? []).map((user) => ({ value: user.id, label: user.full_name })),
    [users]
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  const closeCalendar = useCallback(() => setShowCalendar(false), []);
  useDismiss(calendarRef, showCalendar, closeCalendar);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
      if (!users?.length) return;

      if (selectedUserId === undefined) return;

      const exists = users.some(
        user => user.id === selectedUserId
      );

      if (!exists) {
        setSelectedUserId(undefined);
      }
    }, [users, selectedUserId]);

  const getMonthRange = () => {
    const now = new Date();

    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    );

    const end = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    );

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  };

  const { start, end } = getMonthRange();
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);

  const parseLocalDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);

    return new Date(year, month - 1, day);
  };

  const [range, setRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: parseLocalDate(startDate),
    to: parseLocalDate(endDate),
  });

  const { data, isLoading } = useAdminDashboard(
    startDate,
    endDate,
  );

  const dashboard = data?.getAdminDashboard;

  const selectedOvertimeUser = useMemo(() => {
    if (!selectedUserId) return null;

    return dashboard?.overtime_chart?.find(
      (u) => u.user_id === selectedUserId
    );
  }, [dashboard?.overtime_chart, selectedUserId]);

  const selectedSickdayUser = useMemo(() => {
    if (!selectedUserId) return null;

    return dashboard?.sickday_chart?.find(
      (u) => u.user_id === selectedUserId
    );
  }, [dashboard?.sickday_chart, selectedUserId]);

  const overtimeTotals = useMemo(() => {
    if (!dashboard?.overtime_chart)
      return { done: 0, payed: 0, taken: 0, adjust: 0 };

    const raw = dashboard.overtime_chart.reduce(
      (acc, u) => ({
        done: acc.done + u.overtime_done,
        payed: acc.payed + u.overtime_payed,
        taken: acc.taken + u.overtime_taken,
        adjust: acc.adjust + u.overtime_adjust,
      }),
      { done: 0, payed: 0, taken: 0, adjust: 0 }
    );

    return {
      done: Math.round(raw.done * 100) / 100,
      payed: Math.round(raw.payed * 100) / 100,
      taken: Math.round(raw.taken * 100) / 100,
      adjust: Math.round(raw.adjust * 100) / 100,
    };
  }, [dashboard?.overtime_chart]);

  const totalSickdays = useMemo(() => {
    return (
      dashboard?.sickday_chart?.reduce(
        (sum, u) => sum + u.sickdays,
        0
      ) ?? 0
    );
  }, [dashboard?.sickday_chart]);

  const overtimeDone =
    selectedOvertimeUser?.overtime_done ??
    overtimeTotals.done;

  const overtimePayed =
    selectedOvertimeUser?.overtime_payed ??
    overtimeTotals.payed;

  const overtimeTaken =
    selectedOvertimeUser?.overtime_taken ??
    overtimeTotals.taken;

  const sickdays =
    selectedSickdayUser?.sickdays ??
    totalSickdays;

  const overtimeAdjust =
    selectedOvertimeUser?.overtime_adjust ??
    overtimeTotals.adjust;

  const holidayTaken =
    selectedUserId
      ? dashboard?.holiday_chart?.find((u) => u.user_id === selectedUserId)?.holiday_taken ?? 0
      : dashboard?.holiday_taken ?? 0;

  const formatDateForApi = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');

    return `${y}-${m}-${d}`;
  };

  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('de-DE', {
      timeZone: 'Europe/Berlin',
    });
  };

  const previousMonth = () =>
    setCurrentMonth(
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() - 1,
        1
      )
  );

  const nextMonth = () =>
    setCurrentMonth(
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        1
      )
  );

  const handleDateClick = (date: Date) => {
    if (!range.from || range.to) {
      setRange({
        from: date,
        to: null,
      });

      return;
    }

    const from =
      date < range.from ? date : range.from;

    const to =
      date < range.from ? range.from : date;

    setRange({ from, to });

    setStartDate(formatDateForApi(from));
    setEndDate(formatDateForApi(to));

    setShowCalendar(false);
  };

  const isRangeStart = (date: Date) =>
    range.from &&
    date.toDateString() === range.from.toDateString();

  const isRangeEnd = (date: Date) =>
    range.to &&
    date.toDateString() === range.to.toDateString();

  const isInRange = (date: Date) =>
    range.from &&
    range.to &&
    date >= range.from &&
    date <= range.to;

  const renderCalendar = () => {
    const daysInMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    ).getDate();

    const firstDay =
      (new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        1
      ).getDay() + 6) % 7;

    const dayNames = ['Mo','Di','Mi','Do','Fr','Sa','So'];

    return (
      <>
        {/* HEADER */}
        <div className="flex justify-between mb-4">
          <button onClick={previousMonth}>
            <ChevronLeft />
          </button>

          <div className="font-medium">
            {currentMonth.toLocaleDateString('de-DE',{
              month:'long',
              year:'numeric'
            })}
          </div>

          <button onClick={nextMonth}>
            <ChevronRight />
          </button>
        </div>

        {/* WEEKDAYS */}
        <div className="grid grid-cols-7 text-xs text-center mb-2">
          {dayNames.map(d => <div key={d}>{d}</div>)}
        </div>

        {/* DAYS */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }).map((_,i)=>
            <div key={i}/>
          )}

          {Array.from({ length: daysInMonth }).map((_,i)=>{
            const date = new Date(
              currentMonth.getFullYear(),
              currentMonth.getMonth(),
              i+1
            );

            const inRange =
              range.from &&
              range.to &&
              date >= range.from &&
              date <= range.to;

            return (
              <button
                key={i}
                onClick={()=>handleDateClick(date)}
                className={`aspect-square flex items-center justify-center text-[14px]
                rounded-[8px] transition-colors
                ${
                  isRangeStart(date) || isRangeEnd(date)
                    ? 'bg-[#0084c7] text-white'
                    : isInRange(date)
                    ? 'bg-[#0084c7]/20 text-[#0084c7]'
                    : 'hover:bg-[#f5f5f5]'
                }
                `}
              >
                {i+1}
              </button>
            );
          })}
        </div>
      </>
    );
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <label className="text-[14px] text-[#6b6b6b]">
            Mitarbeiter
          </label>

          <SearchableSelect
            value={selectedUserId}
            onChange={setSelectedUserId}
            options={userOptions}
            emptyOptionLabel="Alle Mitarbeiter"
            searchPlaceholder="Mitarbeiter suchen..."
            noResultsText="Keine Mitarbeiter gefunden"
            className="h-[44px]"
          />
        </div>
        <div ref={calendarRef} className="relative flex items-center gap-3">
        <label className="text-[14px] text-[#6b6b6b]">
          Zeitraum
        </label>

        <input
          readOnly
          onClick={() => setShowCalendar((open) => !open)}
          value={
            range.from && range.to
              ? `${formatDateDisplay(range.from)} - ${formatDateDisplay(range.to)}`
              : 'Datum auswählen'
          }
          className="h-[44px] box-border px-4 pr-10 border border-[#e0e0e0] rounded-lg cursor-pointer"
        />

        <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-[#6b6b6b] pointer-events-none" />

        {showCalendar && (
         <div className="absolute top-full left-0 mt-2 z-40 w-[320px] bg-white border rounded-xl shadow-lg p-4">
            {renderCalendar()}
          </div>
        )}
      </div>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">

        <Card>
          <CardContent>
            <div className="text-[#6b6b6b] text-[14px]">
              Krankheitstage
            </div>
            <div className="text-[28px] font-semibold mt-2">
              {isLoading ? '—' : sickdays}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="text-[#6b6b6b] text-[14px]">
              Überstunden geleistet
            </div>
            <div className="text-[28px] font-semibold mt-2">
              {isLoading ? '—' : overtimeDone}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="text-[#6b6b6b] text-[14px]">
              Überstunden ausgezahlt
            </div>
            <div className="text-[28px] font-semibold mt-2">
              {isLoading ? '—' : overtimePayed}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="text-[#6b6b6b] text-[14px]">
              Überstunden genommen
            </div>
            <div className="text-[28px] font-semibold mt-2">
              {isLoading ? '—' : overtimeTaken}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="text-[#6b6b6b] text-[14px]">
              Überstundenabzug
            </div>
            <div className="text-[28px] font-semibold mt-2">
              {isLoading ? '—' : overtimeAdjust}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="text-[#6b6b6b] text-[14px]">Genommene Urlaubstage</div>
            <div className="text-[28px] font-semibold mt-2">
              {isLoading ? '—' : holidayTaken}
            </div>
          </CardContent>
        </Card>

      </div>
        <OvertimeGraph overtimeChart={dashboard?.overtime_chart ?? []} />
        <SickdayGraph sickdayChart={dashboard?.sickday_chart ?? []} />
        <HolidayGraph holidayChart={data?.getAdminDashboard.holiday_chart || []} />
    </div>
  );
}